<template>
  <div class="ats">
    <div
      v-for="row in rows"
      :key="row"
      class="ats__row"
      :style="{ '--ats-stagger': `${row * 70}ms` }"
    >
      <div
        v-for="(cell, index) in cells"
        :key="index"
        class="ats__cell"
        :class="[
          `ats__cell--${cell.align || (index === 0 ? 'left' : 'center')}`,
          { 'ats__cell--buttons': cell.type === 'buttons' },
        ]"
      >
        <span
          v-for="(bar, barIndex) in barsFor(cell)"
          :key="barIndex"
          class="ats__bar"
          :class="`ats__bar--${bar.shape}`"
          :style="bar.style"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Structural skeleton for app-table rows.
 *
 * Renders placeholder rows on the SAME grid as the real ones (it reads
 * app-table's `--app-table-cols` var), so the shimmer has the shape of the
 * table that is loading instead of a stack of generic bars — the header and
 * column rhythm stay put and nothing jumps when the data lands.
 *
 * Drop it inside <app-table> next to the real rows:
 *
 *   <app-table :columns="gridColumns" :headers="tableHeaders">
 *     <app-table-skeleton v-if="isFetching" :rows="3" :cells="skeletonCells" />
 *     <my-row v-else v-for="row in rows" ... />
 *   </app-table>
 *
 * Each entry in `cells` describes one column:
 *   { type: 'text' | 'code' | 'two-line' | 'pill' | 'buttons', align?, width?, count? }
 */
export default {
  name: 'AppTableSkeleton',

  props: {
    // How many placeholder rows to render.
    rows: {
      type: Number,
      default: 3,
    },

    // One descriptor per column — see the shapes in barsFor().
    cells: {
      type: Array,
      required: true,
    },
  },

  methods: {
    barsFor(cell) {
      const type = cell.type || 'text';

      if (type === 'pill') {
        return [{ shape: 'pill' }];
      }

      if (type === 'buttons') {
        const count = cell.count || 2;

        return Array.from({ length: count }, () => ({ shape: 'button' }));
      }

      if (type === 'two-line') {
        return [
          { shape: 'line', style: { width: cell.width || '55%' } },
          { shape: 'line-sm', style: { width: '85%' } },
        ];
      }

      if (type === 'code') {
        return [{ shape: 'code', style: { width: cell.width || '90%' } }];
      }

      return [{ shape: 'line', style: { width: cell.width || '70%' } }];
    },
  },
};
</script>

<style lang="scss" scoped>
$ats-border: rgba(0, 0, 0, 0.08);

.ats {
  &__row {
    // Same grid as the real rows — app-table publishes it as a CSS var.
    display: grid;
    grid-template-columns: var(--app-table-cols);
    align-items: center;
    // No column gap, for the same reason the real rows have none: app-table's
    // head has no gap, so any gap here would shift the columns out of line.
    padding: 14px 12px;
    border-bottom: 0.5px solid $ats-border;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    padding: 0 8px;

    &--center { align-items: center; }
    &--right  { align-items: flex-end; }

    // Action buttons sit in a row, not stacked like text lines.
    &--buttons {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 4px;
    }
  }

  &__bar {
    display: block;
    background: linear-gradient(90deg, #eef1f4 0%, #f7f9fb 50%, #eef1f4 100%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: ats-shimmer 1.4s ease-in-out infinite;
    animation-delay: var(--ats-stagger, 0ms);

    &--line    { height: 13px; width: 70%; }
    &--line-sm { height: 10px; width: 85%; }
    &--code    { height: 15px; width: 90%; border-radius: 3px; }
    &--pill    { height: 18px; width: 72px; border-radius: 999px; }
    &--button  { height: 22px; width: 64px; border-radius: 6px; }
  }
}

@keyframes ats-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .ats__bar { animation: none; }
}

// Phones: app-table hides its grid head, so rows become stacked cards.
@media (max-width: 600px) {
  .ats__row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 16px;
    margin-bottom: 10px;
    border: 0.5px solid $ats-border;
    border-radius: 10px;

    // The base rule drops the bottom border on the last row (table rows must
    // not have a trailing line). Here each row is a card, so restore it —
    // `.ats__row:last-child` outranks the plain `.ats__row` border above.
    &:last-child {
      margin-bottom: 0;
      border-bottom: 0.5px solid $ats-border;
    }
  }

  .ats__cell {
    padding: 0;

    &--center,
    &--right { align-items: flex-start; }
  }
}
</style>
